import { DatalayerService } from '@analytics/datalayer';
import {
  AnalyticsActionTypes,
  type ParsedToolTrackingConfig,
  type ToolTrackValues,
} from '@analytics/models';
import { AnalyticsUtils } from '@analytics/utils';
import { inject, Injectable, Injector } from '@angular/core';
import { getFieldValue } from '@core/jss-utils';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TrackToolService {
  readonly #datalayer = inject(DatalayerService);
  readonly #utils = inject(AnalyticsUtils);

  readonly store$ = inject(Store<any>);
  readonly injector = inject(Injector);

  public async completeTool(toolConfig: ParsedToolTrackingConfig<any>) {
    const selectorData = await firstValueFrom(
      this.store$.select(toolConfig.toolComplete.selector)
    );
    const toolComplete = toolConfig.toolComplete.onComplete(selectorData);
    const { toolName } = toolConfig;
    const { step, payload } = toolComplete;
    const extraValues = {
      result: payload,
    };

    return this.trackTool(
      {
        toolName,
        step,
        type: AnalyticsActionTypes.LAST_STEP,
      },
      extraValues
    );
  }

  /**
   * @deprecated - please call the datalayer directly. Tools for formatting tool tracking events
   * are available in {@link ToolTrackingUtils}.
   */
  public async trackTool(
    values: ToolTrackValues,
    otherInfo: Record<string, any> = {}
  ) {
    const context = await this.#utils.getSiteContext();
    const toolName = this.#getToolName(values);

    this.#datalayer.pushEvent({
      event: `${values.type}`,
      info: {
        name: this.#utils.prefixWithAbbreviation(toolName.toLowerCase()),
        placement: `${context}|${toolName}`.toLowerCase(),
        step: values.step,
        // This field is intentionally not camelCase, as making it camelcase breaks the integration with Adobe Analytics within Adobe Launch.
        stepname: this.#getStepName(values, toolName),
        ...otherInfo,
      },
    });
  }

  #getStepName(values: ToolTrackValues, toolName = '') {
    const stepName = values.stepName ?? '';

    if (values.type === AnalyticsActionTypes.LAST_STEP) {
      return `${toolName} laststep`;
    }

    return [toolName, stepName].join(' ').trim().toLocaleLowerCase();
  }

  #getToolName(values: ToolTrackValues) {
    return (
      values.toolName ??
      getFieldValue<string>(values.rendering, 'componentName') ??
      values.rendering?.componentName ??
      'unknown-component'
    );
  }
}
